import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CarePlanSectionProps, NA, NO, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  HEALTH_INDICATORS_GENIE_FIELD_DEFINITIONS,
  HEALTH_INDICATORS_GENIE_QUESTIONS,
  HealthIndicatorsGenieSectionValues,
} from "./healthIndicatorsGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const HealthIndicatorSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS,

    anyPain: sectionData?.anyPain || false,
    anyPainStrengthDetails: sectionData?.anyPainStrengthDetails || "",
    anyPainAssistanceDetails: sectionData?.anyPainAssistanceDetails || "",

    anyWeightChanges: sectionData?.anyWeightChanges || false,
    currentWeight: sectionData?.currentWeight || "",
    currentHeight: sectionData?.currentHeight || "",
    anyWeightStrengthDetails: sectionData?.anyWeightStrengthDetails || "",
    anyWeightAssistanceDetails: sectionData?.anyWeightAssistanceDetails || "",

    anyVitalSignIssues: sectionData?.anyVitalSignIssues || false,
    anyVitalSignStrengthDetails: sectionData?.anyVitalSignStrengthDetails || "",
    anyVitalSignAssistanceDetails: sectionData?.anyVitalSignAssistanceDetails || "",

    anyHospitalizations: sectionData?.anyHospitalizations || false,
    anyHospitalizationsStrengthDetails: sectionData?.anyHospitalizationsStrengthDetails || "",
    anyHospitalizationsAssistanceDetails: sectionData?.anyHospitalizationsAssistanceDetails || "",

    anyOtherHealthIndicatorsDetails: sectionData?.anyOtherHealthIndicatorsDetails || "",
    anyOtherHealthIndicatorsStrengthDetails: sectionData?.anyOtherHealthIndicatorsStrengthDetails || "",
    anyOtherHealthIndicatorsAssistanceDetails: sectionData?.anyOtherHealthIndicatorsAssistanceDetails || "",

    anyAllergies: sectionData?.anyAllergies || false,
    allergySubstanceDetails: sectionData?.allergySubstanceDetails || "",
    allergyReactionDetails: sectionData?.allergyReactionDetails || "",
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, getValues, setValue } = useForm({ defaultValues });
  const watchAnyPain = watch('anyPain', defaultValues.anyPain);
  const watchAnyWeightChanges = watch('anyWeightChanges', defaultValues.anyWeightChanges);
  const watchAnyVitalSignIssues = watch('anyVitalSignIssues', defaultValues.anyVitalSignIssues);
  const watchAnyHospitalizations = watch('anyHospitalizations', defaultValues.anyHospitalizations);
  const watchAnyOtherHealthIndicatorsDetails = watch('anyOtherHealthIndicatorsDetails', defaultValues.anyOtherHealthIndicatorsDetails);
  const watchAnyAllergies = watch('anyAllergies', defaultValues.anyAllergies);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const subTitleTemplate = (
    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
      <div>
        <span className="p-float-label" style={{ cursor: 'pointer' }}
          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.HEALTH_INDICATORS_OVERVIEW_DETAILS) }}>
          Guidance for this section <i className="pi pi-info-circle"></i>
        </span>
      </div>
    </div>
  );

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={HEALTH_INDICATORS_GENIE_QUESTIONS}
        fieldDefinitions={HEALTH_INDICATORS_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as HealthIndicatorsGenieSectionValues}
        successMessage="Health indicators care plan updated."
        errorMessage="Unable to update the health indicators care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = {
            ...getValues(),
            ...payload.valueUpdates,
          };

          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });

          await onsubmit(nextValues);
        }}
      />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} subTitle={subTitleTemplate} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Pain details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyPain })}>Any pain:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPain"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPain')}
                    </div>

                    {
                      watchAnyPain && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyPainStrengthDetails" className={classNames({ 'p-error': errors.anyPainStrengthDetails })}>
                                Add pain details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.HEALTH_INDICATORS_ANY_PAIN_DETAILS) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyPainStrengthDetails" control={control}
                                rules={{ required: 'Pain details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add pain details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyPainStrengthDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyPainAssistanceDetails" className={classNames({ 'p-error': errors.anyPainAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyPainAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for pain management"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyPainAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any pain: </strong>{sectionData?.anyPain ? YES : NO}</div>
                    {
                      sectionData?.anyPain && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                            <strong>Pain details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyPainStrengthDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyPainAssistanceDetails || NA)}</span>
                          </div>
                        </>)
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Weight details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyWeightChanges })}>Any weight changes:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyWeightChanges"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyWeightChanges')}
                    </div>

                    {
                      watchAnyWeightChanges && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyWeightStrengthDetails" className={classNames({ 'p-error': errors.anyWeightStrengthDetails })}>
                                Add weight change details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyWeightStrengthDetails" control={control}
                                rules={{ required: 'Weight details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Weight details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyWeightStrengthDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyWeightAssistanceDetails" className={classNames({ 'p-error': errors.anyWeightAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyWeightAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for weight management"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyWeightAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any weight: </strong>{sectionData?.anyWeightChanges ? YES : NO}</div>
                    {
                      sectionData?.anyWeightChanges && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Weight details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyWeightStrengthDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyWeightAssistanceDetails || NA)}</span>
                          </div>
                        </>)
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Vital signs details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyVitalSignIssues })}>Any vital sign issues:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyVitalSignIssues"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyVitalSignIssues')}
                    </div>

                    {
                      watchAnyVitalSignIssues && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyVitalSignStrengthDetails" className={classNames({ 'p-error': errors.anyVitalSignStrengthDetails })}>
                                Add vital sign details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyVitalSignStrengthDetails" control={control}
                                rules={{ required: 'Vital sign details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add vital sign details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyVitalSignStrengthDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyVitalSignAssistanceDetails" className={classNames({ 'p-error': errors.anyVitalSignAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyVitalSignAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for vital sign management"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyVitalSignAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any vital sign: </strong>{sectionData?.anyVitalSignIssues ? YES : NO}</div>
                    {
                      sectionData?.anyVitalSignIssues && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Vital sign details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyVitalSignStrengthDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyVitalSignAssistanceDetails || NA)}</span>
                          </div>
                        </>)
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Hospitalization and emergency visits details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyHospitalizations })}>Any hospitalization:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyHospitalizations"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyHospitalizations')}
                    </div>

                    {
                      watchAnyHospitalizations && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyHospitalizationsStrengthDetails" className={classNames({ 'p-error': errors.anyHospitalizationsStrengthDetails })}>
                                Add hospitalization details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyHospitalizationsStrengthDetails" control={control}
                                rules={{ required: 'Hospitalization details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Hospitalization details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyHospitalizationsStrengthDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyHospitalizationsAssistanceDetails" className={classNames({ 'p-error': errors.anyHospitalizationsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyHospitalizationsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for hospitalization management"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyHospitalizationsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any hospitalizations: </strong>{sectionData?.anyHospitalizations ? YES : NO}</div>
                    {
                      sectionData?.anyHospitalizations && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Hospitalization details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyHospitalizationsStrengthDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyHospitalizationsAssistanceDetails || NA)}</span>
                          </div>
                        </>)
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Any other details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyOtherHealthIndicatorsDetails })}>Any other indicators:</label>

                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.HEALTH_INDICATORS_OTHER_DETAILS) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOtherHealthIndicatorsDetails"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOtherHealthIndicatorsDetails')}
                    </div>

                    {
                      watchAnyOtherHealthIndicatorsDetails && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyOtherHealthIndicatorsStrengthDetails" className={classNames({ 'p-error': errors.anyOtherHealthIndicatorsStrengthDetails })}>
                                Add other health indicators details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyOtherHealthIndicatorsStrengthDetails" control={control}
                                rules={{ required: 'Other health indicators details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add other health indicators details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyOtherHealthIndicatorsStrengthDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anyOtherHealthIndicatorsAssistanceDetails" className={classNames({ 'p-error': errors.anyOtherHealthIndicatorsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anyOtherHealthIndicatorsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for other health indicators"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anyOtherHealthIndicatorsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other health indicators: </strong>{sectionData?.anyOtherHealthIndicatorsDetails ? YES : NO}</div>
                    {
                      sectionData?.anyOtherHealthIndicatorsDetails && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Other health indicators details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyOtherHealthIndicatorsStrengthDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyOtherHealthIndicatorsAssistanceDetails || NA)}</span>
                          </div>
                        </>)
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Allergy details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyAllergies })}>Any allergies:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyAllergies"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyAllergies')}
                    </div>

                    {
                      watchAnyAllergies && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="allergySubstanceDetails" className={classNames({ 'p-error': errors.allergySubstanceDetails })}>
                                Add allergy substance details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="allergySubstanceDetails" control={control}
                                rules={{ required: 'Allergy details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add allergy substance details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('allergySubstanceDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="allergyReactionDetails" className={classNames({ 'p-error': errors.allergyReactionDetails })}>
                                Add any allergy reaction details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="allergyReactionDetails" control={control}
                                rules={{ required: 'Reaction details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for allergy reactions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('allergyReactionDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any allergies: </strong>{sectionData?.anyAllergies ? YES : NO}</div>
                    {
                      sectionData?.anyAllergies && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Allergy substance details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.allergySubstanceDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Allergy reaction details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.allergyReactionDetails || NA)}</span>
                          </div>
                        </>)
                    }
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default HealthIndicatorSection;


